import Column from '@components/Column';
import Image from '@components/Image';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { CouponRow } from '@models/coupon';
import { Link } from '@stackflow/link';
import { pressActionEffect } from '@styles/mixin';
import dayjs from 'dayjs';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
    coupon:CouponRow;
    isHistory?: boolean;
}

function CouponItem({ coupon, isHistory }:Props) {
    const {
        expiresAt, availableShops, couponName, imageUrl, status, couponId,
        tierInfo,
    } = coupon;
    const { t } = useTranslation('coupon');
    const leftDays = dayjs(expiresAt)
        .subtract(1, 'second')
        .diff(dayjs(), 'day');
    const headerText = (() => {
        switch (status) {
        case 'USED': return t('status.used');
        case 'EXPIRED': return t('status.expired');
        case 'CANCELLED': return t('status.cancelled');
        default: return leftDays === 0 ? t('status.expiresToday') : t('status.daysLeft', { days: leftDays });
        }
    })();

    return (
        <Link activityName='CouponDetailActivity'
            activityParams={{ couponId }}
            css={wrapperCss} >
            <Container fullWidth spacing={16} align='center'>
                <ImageWrapper>
                    <Image src={imageUrl} alt={couponName} width={80} height={80} />
                </ImageWrapper>
                <Column spacing={4}>
                    {/* 등급제를 통해 받은 쿠폰일 경우 표시 */}
                    {tierInfo?.displayType === 'BADGE' && (
                        <TierBadge color={tierInfo.textColor}
                            backgroundColor={tierInfo.backgroundColor} >
                            {t('tierBenefit', { name: tierInfo.tierName })}
                        </TierBadge>
                    )}
                    {tierInfo?.displayType === 'ICON' && (
                        <Row spacing={4}>
                            <Image src={tierInfo.iconImageUrl}
                                width={16}
                                height={16} />
                            <Text size='xs' fontWeight='bold'>
                                {t('tierBenefit', { name: tierInfo.tierName })}
                            </Text>
                        </Row>
                    )}
                    {availableShops !== '전체매장' && (
                        <Text size='s'>{availableShops}</Text>
                    )}
                    <Text size='lg' fontWeight='medium'>{couponName}</Text>
                    <Row spacing={8}>
                        {(!isHistory) && (
                            <Badge>
                                <Text size='xs' color={colors.gray8} fontWeight='semibold'>
                                    {headerText}
                                </Text>
                            </Badge>
                        )}
                        <Text color={colors.gray7} size='s'>
                            {t('validity', {
                                date: dayjs(expiresAt)
                                    .subtract(1, 'second')
                                    .format('YYYY.MM.DD'),
                            })}
                        </Text>
                        {(isHistory) && (
                            <Badge>
                                <Text size='xs' color={colors.gray8} fontWeight='semibold'>
                                    {headerText}
                                </Text>
                            </Badge>
                        )}
                    </Row>
                </Column>
            </Container>
        </Link>
    );
}

const Container = styled(Row)`
    position: relative;
`;

const ImageWrapper = styled.div`
    width: 80px;
    height: 80px;
    min-width: 80px;
    background-color: var(--color-gray1);
    border-radius: 8px;
    overflow: hidden;
`;

const Badge = styled.div`
    padding: 2px 4px;
    background-color: var(--color-gray2);
    border-radius: 4px;
`;

const wrapperCss = css`
    width: 100%;
    ${pressActionEffect}
`;

const TierBadge = styled.div<{
    color?:string;
    backgroundColor?: string;
}>`
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 9px;
    font-weight: 500;
    color: ${({ color }) => color || colors.buttonText};
    background-color: ${({ backgroundColor }) => backgroundColor || colors.primary}
`;

export default CouponItem;
